// src/components/Drive/AdminPanels/BusDeliveriesPanel.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { Bus, Car, ChevronDown, ChevronUp, EyeOff, Pencil } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useDelivery } from "../../../context/DeliveryContext";
import { useSearchParams } from "react-router-dom";
import { ExpandedBusDeliveryRow } from "./ExpandedBusDeliveryRow";
import { EditDeliveryModal } from "./EditDeliveryModal";

/* ── Helpers ── */

function fmtMs(ms) {
  if (!ms) return "–";
  try {
    const d = new Date(ms);
    return d.toLocaleDateString("fi-FI", {
      day: "numeric",
      month: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return "–"; }
}

function fmtDuration(seconds) {
  if (!seconds) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h} t ${m} min` : `${m} min`;
}

function truncate(str, len = 22) {
  if (!str) return "–";
  return str.length > len ? str.slice(0, len) + "…" : str;
}

/* ── Derive a readable route from busTransportData ── */
function getBusRoute(delivery) {
  const bd = delivery.busTransportData;
  if (!bd) return { origin: "–", destination: "–" };

  // Try stations first (cleanest names), fall back to first/last leg
  const origin =
    bd.stations?.origin?.name ||
    bd.legs?.[0]?.origin ||
    delivery.origin ||
    "–";
  const destination =
    bd.stations?.destination?.name ||
    bd.legs?.[bd.legs?.length - 1]?.destination ||
    delivery.destination ||
    "–";

  return { origin, destination };
}

/* ── Count leg types ── */
function getLegSummary(busTransportData) {
  const legs = busTransportData?.legs || [];
  const busCnt = legs.filter((l) => l.type === "bus-transport").length;
  const carCnt = legs.filter((l) => l.type === "car").length;
  return { busCnt, carCnt, total: legs.length };
}

/* ── Status chip (re-use same statuses as StatusPanel) ── */
const STATUS_LABELS = {
  active:    { label: "Saatavilla", color: "info" },
  ongoing:   { label: "Meneillään", color: "primary" },
  picked:    { label: "Noudettu",   color: "warning" },
  completed: { label: "Valmis",     color: "success" },
  cancelled: { label: "Peruttu",    color: "default" },
};

function StatusChip({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status || "–", color: "default" };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
}

/* ── Table ── */
function BusDeliveryTable({ deliveries, colors, onOpen, onEdit }) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [expandedId, setExpandedId] = useState(null);

  return (
    <TableContainer
      sx={{
        border: `1px solid ${colors.surface?.border || "rgba(0,0,0,0.12)"}`,
        borderRadius: 2,
        maxHeight: isMobile ? "calc(100vh - 280px)" : 460,
        overflowX: "auto",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", minWidth: isMobile ? 120 : 180 }}>
              Reitti
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", minWidth: 90 }}>Tila</TableCell>
            {!isMobile && (
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Lähtö</TableCell>
            )}
            {!isMobile && (
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Kesto</TableCell>
            )}
            {!isMobile && (
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Hinta</TableCell>
            )}
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }} align="center">
              Toiminnot
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {deliveries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isMobile ? 3 : 6} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Ei bussikuljetuksia.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            deliveries.map((d) => {
              const bd = d.busTransportData;
              const { origin, destination } = getBusRoute(d);
              const { busCnt, carCnt } = getLegSummary(bd);

              return (
                <React.Fragment key={d.id}>
                  {/* ── Main row ── */}
                  <TableRow
                    hover
                    sx={{
                      cursor: "pointer",
                      opacity: d.hide ? 0.55 : 1,
                      "&:last-child td": { borderBottom: 0 },
                    }}
                    onClick={() => onOpen(d.id)}
                  >
                    {/* Route */}
                    <TableCell sx={{ maxWidth: isMobile ? 120 : 200 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title={origin} arrow>
                            <Typography variant="caption" fontWeight={600} noWrap>
                              {truncate(origin, isMobile ? 14 : 22)}
                            </Typography>
                          </Tooltip>
                          {d.hide && (
                            <Tooltip title="Piilotettu" arrow>
                              <EyeOff size={11} opacity={0.4} />
                            </Tooltip>
                          )}
                        </Box>
                        <Tooltip title={destination} arrow>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {truncate(destination, isMobile ? 14 : 22)}
                          </Typography>
                        </Tooltip>
                        {/* Leg icons summary */}
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.25 }}>
                          {carCnt > 0 && (
                            <Tooltip title={`${carCnt} autosiirtymä`} arrow>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                                <Car size={10} opacity={0.5} />
                                <Typography sx={{ fontSize: "0.6rem", opacity: 0.6 }}>{carCnt}</Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {busCnt > 0 && (
                            <Tooltip title={`${busCnt} bussiosuus`} arrow>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                                <Bus size={10} opacity={0.6} color="currentColor" />
                                <Typography sx={{ fontSize: "0.6rem", opacity: 0.6 }}>{busCnt}</Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <StatusChip status={d.status} />
                        {isMobile && bd?.start && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            {fmtMs(bd.start)}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          {d.courierName}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Departure — desktop */}
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmtMs(bd?.start)}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Duration — desktop */}
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDuration(bd?.duration) || "–"}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Price — desktop */}
                    {!isMobile && (
                      <TableCell>
                        {bd?.price != null ? (
                          <Chip
                            label={`${bd.price} €`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: "0.68rem", height: 20, fontWeight: 700 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">–</Typography>
                        )}
                      </TableCell>
                    )}

                    {/* Actions */}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center" }}>
                        <Tooltip title="Muokkaa" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEdit(d); }}
                            sx={{ p: isMobile ? 0.75 : 0.5 }}
                          >
                            <Pencil size={isMobile ? 16 : 14} opacity={0.6} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={expandedId === d.id ? "Sulje" : "Laajenna"} arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(expandedId === d.id ? null : d.id);
                            }}
                            sx={{ p: isMobile ? 0.75 : 0.5 }}
                          >
                            {expandedId === d.id
                              ? <ChevronUp size={isMobile ? 16 : 14} opacity={0.6} />
                              : <ChevronDown size={isMobile ? 16 : 14} opacity={0.6} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* ── Expanded detail row ── */}
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : 6} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedId === d.id} timeout="auto" unmountOnExit>
                        <ExpandedBusDeliveryRow delivery={d} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ── Panel ── */
export function BusDeliveriesPanel() {
  const { allDeliveries, loading: deliveriesLoading } = useDelivery();
  const { colors } = useTheme();
  const [, setSearchParams] = useSearchParams();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deliveryToEdit, setDeliveryToEdit] = useState(null);

  const busDeliveries = useMemo(() => {
    if (!allDeliveries?.length) return [];
    return allDeliveries.filter((d) => d.deliveryTransportType === "eco-bus");
  }, [allDeliveries]);

  const handleOpen = (deliveryId) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("deliveryId", deliveryId);
      p.delete("tab");
      return p;
    }, { replace: false });
  };

  const handleEditClick = (delivery) => { setDeliveryToEdit(delivery); setEditDialogOpen(true); };
  const handleEditClose = () => { setEditDialogOpen(false); setDeliveryToEdit(null); };

  if (deliveriesLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Bus size={16} opacity={0.7} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.secondary" }}>
          Bussikuljetukset ({busDeliveries.length})
        </Typography>
      </Box>

      {busDeliveries.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${colors.surface?.border || "rgba(0,0,0,0.12)"}`,
            borderRadius: 2,
            py: 6,
            textAlign: "center",
          }}
        >
          <Bus size={28} opacity={0.2} style={{ marginBottom: 8 }} />
          <Typography variant="body2" color="text.secondary">
            Ei bussikuljetuksia.
          </Typography>
        </Box>
      ) : (
        <BusDeliveryTable
          deliveries={busDeliveries}
          colors={colors}
          onOpen={handleOpen}
          onEdit={handleEditClick}
        />
      )}

      <EditDeliveryModal
        delivery={deliveryToEdit}
        open={editDialogOpen}
        onClose={handleEditClose}
      />
    </Box>
  );
}